import {
    Fragment,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import { Link } from "react-router-dom";
import Collapse from "react-bootstrap/Collapse";
import Select from "react-select";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

import AttendanceServices from "../../../services/AttendanceServices";
import VehicleServics from "../../../services/VehicleServics";
import Pagination from "../Pagenation/Pagenation";

const getBaseUrl = () =>
    ["localhost", "127.0.0.1", "[::1]"].includes(
        window.location.hostname
    )
        ? "http://localhost:3001"
        : "https://indigoinfra.in";

const getImageUrl = (filename) => {
    if (!filename) return "";

    const name = String(filename).split(/[\\/]/).pop();

    return `${getBaseUrl()}/img/attendance/${encodeURIComponent(name)}`;
};

const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString("en-GB", {
        timeZone: "Asia/Kolkata",
        hour12: false
    });
};

const getNumber = (value) => {
    if (
        value === undefined ||
        value === null ||
        String(value).trim() === ""
    ) {
        return null;
    }

    const number = Number(value);

    return Number.isFinite(number) ? number : null;
};

const getHMR = (item) => {
    const start = getNumber(item.startKM);
    const end = getNumber(item.endKM);

    return start === null || end === null
        ? null
        : end - start;
};

const getDieselQty = (item) =>
    (item.desile || []).reduce(
        (total, entry) =>
            total + (getNumber(entry.desileQty) ?? 0),
        0
    );

const approvalLabel = (value) =>
    ({
        pending: "Pending",
        approved: "Approved",
        rejected: "Rejected"
    }[value || "pending"] || value);

const attendanceLabel = (value) =>
    ({
        checkIn: "Checked In",
        checkOut: "Checked Out"
    }[value] || value || "-");

const rowOptions = [10, 20, 50, 100, 200].map((value) => ({
    value,
    label: `${value} Rows`
}));

const ApprovalDetails = ({
    status,
    reviewer,
    date,
    remark
}) => {
    const value = status || "pending";

    const color =
        value === "approved"
            ? "bg-success"
            : value === "rejected"
                ? "bg-danger"
                : "bg-warning text-dark";

    return (
        <div>
            <span className={`badge ${color}`}>
                {approvalLabel(value)}
            </span>

            {reviewer && (
                <div className="small mt-1">
                    <strong>By: </strong>
                    {reviewer}
                </div>
            )}

            {date && (
                <div className="small mt-1">
                    {formatDate(date)}
                </div>
            )}

            {remark && (
                <div className="small mt-1">
                    <strong>Remark: </strong>
                    {remark}
                </div>
            )}
        </div>
    );
};

const MeterImage = ({ filename, label }) => {
  const [failed, setFailed] = useState(false);
  const url = getImageUrl(filename);

  useEffect(() => {
    setFailed(false);
  }, [url]);

  if (!url || failed) {
    return (
      <span className="text-muted small">
        {failed ? "Image not available" : "No image"}
      </span>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="attendance-report-photo-link"
      title={label}
    >
      <img
        src={url}
        alt={label}
        className="attendance-report-meter-photo"
        loading="lazy"
        onError={() => setFailed(true)}
      />

      <span className="small d-block mt-1">
        {label}
      </span>
    </a>
  );
};

const AttendenceReport = () => {
    const [vehicles, setVehicles] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState("all");
    const [vehicleError, setVehicleError] = useState("");

    const [open, setOpen] = useState(true);
    const [open2, setOpen2] = useState(true);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasGenerated, setHasGenerated] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const [sort, setSort] = useState(20);
    const [activePage, setActivePage] = useState(0);
    const [expandedRow, setExpandedRow] = useState(null);

    const scrollRef = useRef(null);
    const requestLock = useRef(false);

    useEffect(() => {
        let cancelled = false;

        const loadVehicles = async () => {
            try {
                const response =
                    await VehicleServics.getVehicle();

                const items = Array.isArray(response?.data?.data)
                    ? response.data.data
                    : Array.isArray(response?.data)
                        ? response.data
                        : null;

                if (!items) {
                    throw new Error(
                        "Unexpected machine list response."
                    );
                }

                if (!cancelled) {
                    setVehicles(items);
                }
            } catch (error) {
                if (!cancelled) {
                    setVehicleError(
                        error?.response?.data?.message ||
                        error.message ||
                        "Failed to load machines."
                    );
                }
            }
        };

        loadVehicles();

        return () => {
            cancelled = true;
        };
    }, []);

    const vehicleOptions = useMemo(
        () => [
            {
                value: "all",
                label: "All Machines"
            },
            ...vehicles
                .map((vehicle) => {
                    const asset = vehicle.asset;

                    const equipmentNo =
                        asset?.equipmentNo ||
                        vehicle.equipmentNo ||
                        "";

                    const description =
                        asset?.masterItem?.description ||
                        vehicle.vehicleName ||
                        vehicle.name ||
                        "";

                    return {
                        value: String(vehicle._id),
                        label:
                            [equipmentNo, description]
                                .filter(Boolean)
                                .join(" | ") ||
                            String(vehicle._id)
                    };
                })
                .sort((a, b) =>
                    a.label.localeCompare(b.label)
                )
        ],
        [vehicles]
    );

    const filteredData = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();

        if (!term) return reportData;

        return reportData.filter((item) => {
            const asset = item.vehicle?.asset;

            return [
                item.store?.name,
                item.driver?.name,
                asset?.masterItem?.partNo,
                asset?.masterItem?.description,
                asset?.equipmentNo,
                asset?.model,
                asset?.serialNumber,
                asset?.store?.name,
                attendanceLabel(item.status),
                approvalLabel(item.approvalStatus),
                approvalLabel(item.dieselApprovalStatus),
                item.approvedBy?.name,
                item.dieselReviewedBy?.name,
                item.approvalRemark,
                item.dieselApprovalRemark
            ]
                .filter(
                    (value) =>
                        value !== null &&
                        value !== undefined
                )
                .join(" ")
                .toLowerCase()
                .includes(term);
        });
    }, [reportData, searchTerm]);

    const totalPages = Math.ceil(filteredData.length / sort);

    const currentPage = Math.min(
        activePage,
        Math.max(0, totalPages - 1)
    );

    const start = currentPage * sort;

    const visibleEntries = filteredData.slice(
        start,
        start + sort
    );

    const totals = useMemo(
        () =>
            filteredData.reduce(
                (result, item) => ({
                    hmr: result.hmr + (getHMR(item) ?? 0),
                    diesel: result.diesel + getDieselQty(item)
                }),
                {
                    hmr: 0,
                    diesel: 0
                }
            ),
        [filteredData]
    );

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }

        setExpandedRow(null);
    }, [currentPage, sort, searchTerm, reportData]);

    const clearReport = () => {
        setReportData([]);
        setHasGenerated(false);
        setExpandedRow(null);
        setActivePage(0);
    };

    const fetchReport = async () => {
        if (requestLock.current) return;

        if (!fromDate || !toDate || fromDate > toDate) {
            await Swal.fire(
                "Invalid Dates",
                "Select both dates. From Date must be on or before To Date.",
                "warning"
            );
            return;
        }

        requestLock.current = true;
        setLoading(true);
        setExpandedRow(null);

        try {
            const response =
                await AttendanceServices.attendenceReport({
                    fromDate,
                    toDate,
                    vehicle: selectedVehicle
                });

            if (!Array.isArray(response?.data)) {
                throw new Error(
                    "Unexpected report response."
                );
            }

            setReportData(response.data);
            setHasGenerated(true);
            setActivePage(0);
        } catch (error) {
            clearReport();

            await Swal.fire(
                "Error",
                error?.response?.data?.message ||
                    error.message ||
                    "Failed to generate report.",
                "error"
            );
        } finally {
            requestLock.current = false;
            setLoading(false);
        }
    };

    const resetAll = () => {
        setFromDate("");
        setToDate("");
        setSelectedVehicle("all");
        setSearchTerm("");
        setSort(20);
        clearReport();
    };

    const handlePageChange = (page) => {
        if (
            loading ||
            !Number.isInteger(page) ||
            page < 0 ||
            page >= totalPages
        ) {
            return;
        }

        setActivePage(page);
    };

    const downloadExcel = () => {
        if (!filteredData.length) return;

        const rows = filteredData.map((item, index) => {
            const asset = item.vehicle?.asset;

            return {
                "S.No": index + 1,
                "Store": item.store?.name || "",
                "Part No": asset?.masterItem?.partNo ?? "",
                "Description":
                    asset?.masterItem?.description || "",
                "Equipment No": asset?.equipmentNo || "",
                "Model": asset?.model || "",
                "Serial Number": asset?.serialNumber || "",
                "Machine Store": asset?.store?.name || "",
                "Driver": item.driver?.name || "",

                "Start Date & Time (IST)":
                    formatDate(item.startTime),
                "End Date & Time (IST)":
                    formatDate(item.endTime),

                "Start HMR": getNumber(item.startKM) ?? "",
                "End HMR": getNumber(item.endKM) ?? "",
                "Worked HMR": getHMR(item) ?? "",

                "Diesel In Reading":
                    getNumber(item.dieselInReading) ?? "",
                "Diesel Out Reading":
                    getNumber(item.dieselOutReading) ?? "",

                "Diesel Fill-Up Qty": getDieselQty(item),

                "Diesel Fill-Up Details": (item.desile || [])
                    .map(
                        (entry) =>
                            `Qty: ${entry.desileQty ?? "-"}, ` +
                            `HMR: ${entry.reading ?? "-"}, ` +
                            `Date: ${formatDate(entry.diseleFilupDate)}`
                    )
                    .join("\n"),

                "Status": attendanceLabel(item.status),

                "Attendance Approval":
                    approvalLabel(item.approvalStatus),
                "Attendance Reviewed By":
                    item.approvedBy?.name || "",
                "Attendance Reviewed At (IST)":
                    formatDate(item.approvedAt),
                "Attendance Remark":
                    item.approvalRemark || "",

                "Diesel Approval":
                    approvalLabel(item.dieselApprovalStatus),
                "Diesel Reviewed By":
                    item.dieselReviewedBy?.name || "",
                "Diesel Reviewed At (IST)":
                    formatDate(item.dieselReviewedAt),
                "Diesel Remark":
                    item.dieselApprovalRemark || "",

                "Check-in Image":
                    getImageUrl(item.meterPhotos?.start),
                "Checkout Image":
                    getImageUrl(item.meterPhotos?.end),

                "Updated At (IST)":
                    formatDate(item.updatedAt)
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const headers = Object.keys(rows[0]);

        ["Check-in Image", "Checkout Image"].forEach(
            (header) => {
                const column = headers.indexOf(header);

                rows.forEach((row, index) => {
                    const url = row[header];

                    if (!url) return;

                    const address = XLSX.utils.encode_cell({
                        r: index + 1,
                        c: column
                    });

                    worksheet[address].l = {
                        Target: url,
                        Tooltip: "Open meter image"
                    };
                });
            }
        );

        worksheet["!cols"] = headers.map((header) => ({
            wch: header.includes("Image")
                ? 55
                : header.includes("Details")
                    ? 65
                    : 24
        }));

        worksheet["!autofilter"] = {
            ref: worksheet["!ref"]
        };

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Machine Log"
        );

        const timestamp = new Date()
            .toISOString()
            .replace(/[:.]/g, "-");

        XLSX.writeFile(
            workbook,
            `Machine_Log_${fromDate}_${toDate}_${timestamp}.xlsx`
        );
    };

    return (
        <div className="col-xl-12">
            <div className="filter cm-content-box box-primary">
                <div className="content-title">
                    <div className="cpa">
                        <i className="fas fa-filter me-2" />
                        Filter
                    </div>

                    <div className="tools">
                        <Link
                            to="#"
                            className={`SlideToolHeader ${
                                open ? "collapse" : "expand"
                            }`}
                            onClick={(event) => {
                                event.preventDefault();
                                setOpen((previous) => !previous);
                            }}
                            aria-label="Toggle filters"
                            aria-expanded={open}
                        >
                            <i className="fas fa-angle-up" />
                        </Link>
                    </div>
                </div>

                <Collapse in={open}>
                    <div className="cm-content-body form excerpt">
                        <div className="card-body attendance-report-filters">
                            {vehicleError && (
                                <div className="alert alert-warning">
                                    {vehicleError}
                                </div>
                            )}

                            <div className="row g-2 align-items-end">
                                <div className="col-12 col-md-6 col-xl-2">
                                    <label
                                        htmlFor="report-from"
                                        className="form-label mb-1"
                                    >
                                        From Date
                                    </label>

                                    <input
                                        id="report-from"
                                        type="date"
                                        className="form-control"
                                        value={fromDate}
                                        max={toDate || undefined}
                                        disabled={loading}
                                        onChange={(event) => {
                                            setFromDate(event.target.value);
                                            clearReport();
                                        }}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-xl-2">
                                    <label
                                        htmlFor="report-to"
                                        className="form-label mb-1"
                                    >
                                        To Date
                                    </label>

                                    <input
                                        id="report-to"
                                        type="date"
                                        className="form-control"
                                        value={toDate}
                                        min={fromDate || undefined}
                                        disabled={loading}
                                        onChange={(event) => {
                                            setToDate(event.target.value);
                                            clearReport();
                                        }}
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-xl-4">
                                    <label
                                        htmlFor="report-machine"
                                        className="form-label mb-1"
                                    >
                                        Machine
                                    </label>

                                    <Select
                                        inputId="report-machine"
                                        options={vehicleOptions}
                                        value={
                                            vehicleOptions.find(
                                                (option) =>
                                                    option.value === selectedVehicle
                                            ) || vehicleOptions[0]
                                        }
                                        onChange={(option) => {
                                            setSelectedVehicle(
                                                option?.value || "all"
                                            );
                                            clearReport();
                                        }}
                                        isSearchable
                                        isDisabled={loading}
                                        className="custom-react-select"
                                        classNamePrefix="machine-report-select"
                                        menuPlacement="auto"
                                    />
                                </div>

                                <div className="col-12 col-md-3 col-xl-2">
                                    <button
                                        type="button"
                                        className="btn btn-primary w-100 attendance-report-filter-btn"
                                        disabled={loading}
                                        onClick={fetchReport}
                                    >
                                        {loading
                                            ? "Loading..."
                                            : "Generate Report"}
                                    </button>
                                </div>

                                <div className="col-12 col-md-3 col-xl-2">
                                    <button
                                        type="button"
                                        className="btn btn-secondary w-100 attendance-report-filter-btn"
                                        disabled={loading}
                                        onClick={resetAll}
                                    >
                                        Reset All
                                    </button>
                                </div>
                            </div>

                            <div className="small text-muted mt-2">
                                Date range uses record creation date in IST.
                            </div>
                        </div>
                    </div>
                </Collapse>
            </div>

            <div className="filter cm-content-box box-primary">
                <div className="content-title">
                    <div className="cpa">
                        <i className="fa fa-list me-2" />
                        Machine Log

                        <span className="badge bg-warning ms-2">
                            {filteredData.length}
                        </span>
                    </div>

                    <div className="tools">
                        <Link
                            to="#"
                            className={`SlideToolHeader ${
                                open2 ? "collapse" : "expand"
                            }`}
                            onClick={(event) => {
                                event.preventDefault();
                                setOpen2((previous) => !previous);
                            }}
                            aria-label="Toggle report"
                            aria-expanded={open2}
                        >
                            <i className="fas fa-angle-up" />
                        </Link>
                    </div>
                </div>

                <Collapse in={open2}>
                    <div className="cm-content-body form excerpt">
                        <div className="card-body">
                            <div className="row g-2 align-items-end mb-3 attendance-report-toolbar">
                                <div className="col-12 col-md-6 col-xl-4">
                                    <label
                                        htmlFor="report-search"
                                        className="form-label mb-1"
                                    >
                                        Search Report
                                    </label>

                                    <input
                                        id="report-search"
                                        type="search"
                                        className="form-control"
                                        placeholder="Search store / machine / driver / status"
                                        value={searchTerm}
                                        disabled={loading}
                                        onChange={(event) => {
                                            setSearchTerm(event.target.value);
                                            setActivePage(0);
                                        }}
                                    />
                                </div>

                                <div className="col-12 col-md-3 col-xl-2">
                                    <label
                                        htmlFor="report-rows"
                                        className="form-label mb-1"
                                    >
                                        Rows
                                    </label>

                                    <Select
                                        inputId="report-rows"
                                        options={rowOptions}
                                        value={rowOptions.find(
                                            (option) =>
                                                option.value === sort
                                        )}
                                        onChange={(option) => {
                                            setSort(option?.value || 20);
                                            setActivePage(0);
                                        }}
                                        isSearchable={false}
                                        isDisabled={loading}
                                        className="custom-react-select"
                                        classNamePrefix="machine-report-select"
                                        menuPlacement="auto"
                                    />
                                </div>

                                <div className="col-12 col-md-3 col-xl-3">
                                    <div className="small">
                                        <strong>Total HMR: </strong>
                                        {totals.hmr.toFixed(2)}
                                    </div>

                                    <div className="small">
                                        <strong>Fill-Up Qty: </strong>
                                        {totals.diesel.toFixed(2)}
                                    </div>
                                </div>

                                <div className="col-12 col-xl-3 text-xl-end">
                                    <button
                                        type="button"
                                        className="btn btn-primary attendance-report-filter-btn"
                                        disabled={
                                            loading ||
                                            !filteredData.length
                                        }
                                        onClick={downloadExcel}
                                    >
                                        <i className="fas fa-download me-2" />
                                        Download Excel
                                    </button>
                                </div>
                            </div>

                            {loading && (
                                <div
                                    className="text-center mb-3"
                                    role="status"
                                >
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Loading report...
                                </div>
                            )}

                            <div className="table-responsive attendance-report-table-wrapper">
                                <div className="dataTables_wrapper no-footer">
                                    <div
                                        ref={scrollRef}
                                        className="scrolltable attendance-report-scroll"
                                        tabIndex={0}
                                        role="region"
                                        aria-label="Machine log report table"
                                    >
                                        <table
                                            id="attendance-report-table"
                                            className="table table-bordered table-responsive-lg table-striped table-condensed flip-content"
                                            aria-busy={loading}
                                        >
                                            <thead className="sticky-table-header">
                                                <tr>
                                                    <th>S.No</th>
                                                    <th>Machine Details</th>
                                                    <th>Date &amp; Time (IST)</th>
                                                    <th>HMR</th>
                                                    <th>Diesel Readings</th>
                                                    <th>Diesel Fill-Ups</th>
                                                    <th>Status</th>
                                                    <th>Attendance Approval</th>
                                                    <th>Diesel Approval</th>
                                                    <th>Check-in Image</th>
                                                    <th>Checkout Image</th>
                                                    <th>Updated At</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {visibleEntries.map(
                                                    (item, index) => {
                                                        const asset =
                                                            item.vehicle?.asset;

                                                        const hmr =
                                                            getHMR(item);

                                                        const expanded =
                                                            expandedRow === item._id;

                                                        return (
                                                            <Fragment key={item._id}>
                                                                <tr>
                                                                    <td>
                                                                        {start + index + 1}
                                                                    </td>

                                                                    <td className="attendance-report-machine">
                                                                        <div>
                                                                            <strong>Store: </strong>
                                                                            {item.store?.name || "-"}
                                                                        </div>

                                                                        <div>
                                                                            <strong>Part No: </strong>
                                                                            {asset?.masterItem?.partNo ?? "-"}
                                                                        </div>

                                                                        <div>
                                                                            <strong>Description: </strong>
                                                                            {asset?.masterItem?.description || "-"}
                                                                        </div>

                                                                        <div>
                                                                            <strong>Equipment No: </strong>
                                                                            {asset?.equipmentNo || "-"}
                                                                        </div>

                                                                        <div>
                                                                            <strong>Model: </strong>
                                                                            {asset?.model || "-"}
                                                                        </div>

                                                                        <div>
                                                                            <strong>Driver: </strong>
                                                                            {item.driver?.name || "-"}
                                                                        </div>
                                                                    </td>

                                                                    <td className="text-nowrap">
                                                                        <div>
                                                                            <strong>Start: </strong>
                                                                            {formatDate(item.startTime)}
                                                                        </div>
                                                                        <div>
                                                                            <strong>End: </strong>
                                                                            {formatDate(item.endTime)}
                                                                        </div>
                                                                    </td>

                                                                    <td className="text-nowrap">
                                                                        <div>
                                                                            <strong>Start: </strong>
                                                                            {item.startKM ?? "-"}
                                                                        </div>
                                                                        <div>
                                                                            <strong>End: </strong>
                                                                            {item.endKM ?? "-"}
                                                                        </div>
                                                                        <div>
                                                                            <strong>Worked: </strong>
                                                                            {hmr === null
                                                                                ? "-"
                                                                                : hmr.toFixed(2)}
                                                                        </div>
                                                                    </td>

                                                                    <td className="text-nowrap">
                                                                        <div>
                                                                            <strong>In: </strong>
                                                                            {item.dieselInReading ?? "-"}
                                                                        </div>
                                                                        <div>
                                                                            <strong>Out: </strong>
                                                                            {item.dieselOutReading ?? "-"}
                                                                        </div>
                                                                    </td>

                                                                    <td>
                                                                        <div>
                                                                            <strong>Qty: </strong>
                                                                            {getDieselQty(item).toFixed(2)}
                                                                        </div>

                                                                        {item.desile?.length > 0 && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-primary btn-sm mt-2"
                                                                                onClick={() =>
                                                                                    setExpandedRow(
                                                                                        expanded
                                                                                            ? null
                                                                                            : item._id
                                                                                    )
                                                                                }
                                                                            >
                                                                                {expanded
                                                                                    ? "Hide Details"
                                                                                    : "Show Details"}
                                                                            </button>
                                                                        )}
                                                                    </td>

                                                                    <td>
                                                                        {attendanceLabel(item.status)}
                                                                    </td>

                                                                    <td>
                                                                        <ApprovalDetails
                                                                            status={item.approvalStatus}
                                                                            reviewer={item.approvedBy?.name}
                                                                            date={item.approvedAt}
                                                                            remark={item.approvalRemark}
                                                                        />
                                                                    </td>

                                                                    <td>
                                                                        <ApprovalDetails
                                                                            status={item.dieselApprovalStatus}
                                                                            reviewer={item.dieselReviewedBy?.name}
                                                                            date={item.dieselReviewedAt}
                                                                            remark={item.dieselApprovalRemark}
                                                                        />
                                                                    </td>

                                                                    <td>
                                                                        <MeterImage
                                                                            filename={item.meterPhotos?.start}
                                                                            label="Check-in Photo"
                                                                        />
                                                                    </td>

                                                                    <td>
                                                                        <MeterImage
                                                                            filename={item.meterPhotos?.end}
                                                                            label="Checkout Photo"
                                                                        />
                                                                    </td>

                                                                    <td className="text-nowrap">
                                                                        {formatDate(item.updatedAt)}
                                                                    </td>
                                                                </tr>

                                                                {expanded && (
                                                                    <tr>
                                                                        <td colSpan={12}>
                                                                            <strong>
                                                                                Operator Diesel Fill-Up Details
                                                                            </strong>

                                                                            <table className="table table-bordered mt-2 mb-0">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th>#</th>
                                                                                        <th>Quantity</th>
                                                                                        <th>HMR</th>
                                                                                        <th>Date &amp; Time (IST)</th>
                                                                                    </tr>
                                                                                </thead>

                                                                                <tbody>
                                                                                    {(item.desile || []).map(
                                                                                        (entry, entryIndex) => (
                                                                                            <tr key={entry._id || entryIndex}>
                                                                                                <td>{entryIndex + 1}</td>
                                                                                                <td>{entry.desileQty ?? "-"}</td>
                                                                                                <td>{entry.reading ?? "-"}</td>
                                                                                                <td>
                                                                                                    {formatDate(
                                                                                                        entry.diseleFilupDate
                                                                                                    )}
                                                                                                </td>
                                                                                            </tr>
                                                                                        )
                                                                                    )}
                                                                                </tbody>
                                                                            </table>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </Fragment>
                                                        );
                                                    }
                                                )}

                                                {!visibleEntries.length && (
                                                    <tr>
                                                        <td
                                                            colSpan={12}
                                                            className="text-center"
                                                        >
                                                            {loading
                                                                ? "Loading report..."
                                                                : hasGenerated
                                                                    ? "No matching records found."
                                                                    : "Select dates and generate the report."}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                                        <div className="dataTables_info">
                                            Showing{" "}
                                            {filteredData.length
                                                ? start + 1
                                                : 0}
                                            {" "}to{" "}
                                            {Math.min(
                                                start + sort,
                                                filteredData.length
                                            )}
                                            {" "}of{" "}
                                            {filteredData.length} entries
                                        </div>

                                        {totalPages > 0 && (
                                            <Pagination
                                                activePage={currentPage}
                                                totalPages={totalPages}
                                                onPageChange={handlePageChange}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Collapse>
            </div>
        </div>
    );
};

export default AttendenceReport;